// @ts-nocheck
import axios from 'axios';

export const SUBSCRIPTION_REQUIRED_EVENT = 'appealbot:subscription-required';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

const buildApiUrl = (path) => {
  const normalizedPath = `/${String(path || '').replace(/^\/+/, '')}`;
  return API_BASE_URL ? `${API_BASE_URL}${normalizedPath}` : normalizedPath;
};

const api = axios.create({
  baseURL: API_BASE_URL || undefined,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track refresh token requests to prevent race conditions
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const isSubscriptionRequiredError = (error) =>
  error?.response?.data?.error_type === 'subscription_required';

const emitSubscriptionRequired = (error) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT, {
    detail: error.response?.data ?? null,
  }));
};

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (isSubscriptionRequiredError(error)) {
      error.subscriptionRequired = true;
      error.subscription = error.response?.data ?? null;
      emitSubscriptionRequired(error);
      return Promise.reject(error);
    }

    // If the error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(buildApiUrl('/accounts/auth/token/refresh/'), {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data || {};
        if (!access) {
          throw new Error('Token refresh response did not include an access token');
        }
        localStorage.setItem('token', access); // eslint-disable-line no-restricted-properties
        if (refresh) {
          localStorage.setItem('refreshToken', refresh); // eslint-disable-line no-restricted-properties
        }

        // Process queued requests
        processQueue(null, access);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Process queued requests with error
        processQueue(refreshError, null);

        // If refresh token fails, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
